// Synchronized port of operate-ui/src/lib/content.ts. Kept here so
// atlas-ui doesn't need a shared package — the four products' UIs
// otherwise have no overlap. If you change the response shape on the
// server, update operate-ui, curate-ui, and atlas-ui together.
//
// The server-side endpoint is at /api/v1/content/children, contributed
// by platform:surface:surface-rest's ContentBrowseAutoConfiguration.
// Hand-written here against /api/v1 instead of layered on top of
// atlas's /api/v1/atlas baseURL.

import axios from "axios";
import { getAuthToken } from "./api";

const contentApi = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
});

contentApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export type ContentNode = {
  id: string | null;
  path: string;
  name: string;
  objectClass: string;
  container: boolean;
};

export type BrowseResult = {
  parent: string;
  children: ContentNode[];
};

/**
 * List immediate children of a Cognos content-store path. Pass an
 * undefined / empty / "/" path to get the synthetic root areas
 * (/content, /directory, /configuration). The backend handles XPath
 * segment counting, so the caller just chains the returned
 * {@code path} back in to drill down.
 */
export async function listContentChildren(
  path?: string,
  limit = 500,
): Promise<BrowseResult> {
  const params = new URLSearchParams();
  if (path) params.set("path", path);
  params.set("limit", String(limit));
  const { data } = await contentApi.get<BrowseResult>(
    `/content/children?${params.toString()}`,
  );
  return data;
}

/**
 * Extract a human-friendly label from a Cognos path segment.
 * {@code folder[@name='Reports']} → {@code Reports}; bare atoms
 * ({@code content}, {@code directory}) come back unchanged.
 */
export function labelOfSegment(segment: string): string {
  const m = /\[@name=['"](.+?)['"]\]/.exec(segment);
  return m ? m[1] : segment;
}

/**
 * Split a Cognos search path into ordered segments. XPath predicates
 * ({@code [@name='…']}) can contain characters that would confuse
 * naive splitting, so bracket depth gates the separator.
 */
export function splitPath(path: string): string[] {
  if (!path || !path.startsWith("/")) return [];
  const segments: string[] = [];
  let buf = "";
  let depth = 0;
  for (let i = 1; i < path.length; i++) {
    const c = path[i];
    if (c === "[") depth++;
    else if (c === "]") depth--;
    if (c === "/" && depth === 0) {
      if (buf) segments.push(buf);
      buf = "";
    } else {
      buf += c;
    }
  }
  if (buf) segments.push(buf);
  return segments;
}

export type Crumb = { label: string; path: string };

export function buildBreadcrumbs(path: string): Crumb[] {
  const segments = splitPath(path);
  const crumbs: Crumb[] = [];
  let acc = "";
  for (const seg of segments) {
    acc += "/" + seg;
    crumbs.push({ label: labelOfSegment(seg), path: acc });
  }
  return crumbs;
}

export function parentPath(path: string): string | null {
  if (!path || path === "/") return null;
  const crumbs = buildBreadcrumbs(path);
  if (crumbs.length === 0) return null;
  if (crumbs.length === 1) return "/";
  return crumbs[crumbs.length - 2].path;
}

/** Pretty-print a path for display in a chip (last segment's name). */
export function displayName(path: string): string {
  if (!path || path === "/") return "root";
  const crumbs = buildBreadcrumbs(path);
  return crumbs.length === 0 ? path : crumbs[crumbs.length - 1].label;
}
